import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { X, Info } from "lucide-react";
import { SEOSettings } from "../SEOSettings";
import { ProFeatureWrapper } from "../ProFeatureWrapper";
import { SocialMediaSettings } from "../SocialMediaSettings";
import { ImageDropzone } from "../ImageDropzone";
import { IntegrationSettings } from "./IntegrationSettings";
import { PrivacySettings } from "./PrivacySettings";
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { getThemeColors } from "@/utils/getThemeColors";
import type { FunnelData } from "../types";
import { LogoMakerSettings } from "./LogoMaker/LogoMakerSettings";
import { LOGO_FONTS, FONT_SIZES, FONT_WEIGHTS } from "./LogoMaker/constants";
interface GeneralTabContentProps {
  data: FunnelData;
  onUpdate: (updates: Partial<FunnelData>) => void;
  handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'headerImage') => void;
}
export const GeneralTabContent = ({
  data,
  onUpdate,
  handleImageUpload
}: GeneralTabContentProps) => {
  const handleRemoveLogo = () => {
    onUpdate({
      logo: ""
    });
  };
  const handleRemoveFavicon = () => {
    onUpdate({
      favicon: ""
    });
  };
  const colors = getThemeColors(data);
  return <Tabs defaultValue="general-data" className="w-full">
      <TabsList className="w-full grid grid-cols-3">
        <TabsTrigger value="general-data">Logó</TabsTrigger>
        <TabsTrigger value="appearance">Megjelenés és SEO</TabsTrigger>
        <TabsTrigger value="integrations">Integráció</TabsTrigger>
      </TabsList>

      <Card className="mt-4 p-4 py-[10px] px-[10px] my-[10px]">
        <TabsContent value="general-data" className="space-y-4">
          <LogoMakerSettings data={data} onUpdate={onUpdate} colors={colors} LOGO_FONTS={LOGO_FONTS} FONT_SIZES={FONT_SIZES} FONT_WEIGHTS={FONT_WEIGHTS} />

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label htmlFor="logo">Egyedi Logó (Opcionális)</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-slate-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Recommended size: 200x60px</p>
                      <p>Maximum file size: 5MB</p>
                      <p>Formats: PNG, JPG, SVG</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {data.logo && <Button variant="ghost" size="sm" onClick={handleRemoveLogo} className="h-8 px-2 text-destructive hover:text-destructive/90">
                  <X className="h-4 w-4 mr-1" />
                  Remove
                </Button>}
            </div>
            <ImageDropzone value={data.logo} onChange={base64 => onUpdate({
            logo: base64
          })} onInputChange={e => handleImageUpload(e, 'logo')} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label htmlFor="favicon">Favicon</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-slate-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Recommended size: 32x32px or 16x16px</p>
                      <p>Maximum file size: 5MB</p>
                      <p>Formats: ICO, PNG, SVG</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {data.favicon && <Button variant="ghost" size="sm" onClick={handleRemoveFavicon} className="h-8 px-2 text-destructive hover:text-destructive/90">
                  <X className="h-4 w-4 mr-1" />
                  Remove
                </Button>}
            </div>
            <ImageDropzone value={data.favicon} onChange={base64 => onUpdate({
            favicon: base64
          })} />
          </div>
        </TabsContent>

        <TabsContent value="appearance" className="space-y-4">
          <Tabs defaultValue="seo">
            <TabsList className="w-full grid grid-cols-3">
              <TabsTrigger value="seo">SEO</TabsTrigger>
              <TabsTrigger value="social">Szociális média</TabsTrigger>
              <TabsTrigger value="privacy">Adatkezelés</TabsTrigger>
            </TabsList>
            <TabsContent value="seo">
              <SEOSettings settings={data.seoConfig || {
              title: "",
              metaKeywords: "",
              metaDescription: "",
              ogImage: ""
            }} onChange={seoConfig => onUpdate({
              seoConfig
            })} />
            </TabsContent>
            <TabsContent value="social">
              <SocialMediaSettings links={data.socialMedia || []} onChange={socialMedia => onUpdate({
              socialMedia
            })} isPro={data.isPro || false} />
            </TabsContent>
            <TabsContent value="privacy">
              <PrivacySettings settings={data.privacyConfig || {
              enabled: false,
              title: "Privacy Policy",
              content: "",
              cookieConsentText: "We use cookies to improve your experience on our website. By continuing to browse, you agree to our use of cookies.",
              acceptButtonText: "Accept",
              learnMoreButtonText: "Learn More"
            }} onChange={privacyConfig => onUpdate({
              privacyConfig
            })} />
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="integrations" className="space-y-4">
          <ProFeatureWrapper feature="integrations" isPro={data.isPro || false}>
            <IntegrationSettings settings={data.integrationConfig || {
            headCode: '',
            bodyCode: ''
          }} onChange={integrationConfig => onUpdate({
            integrationConfig
          })} isPro={data.isPro || false} />
          </ProFeatureWrapper>
        </TabsContent>
      </Card>
    </Tabs>;
};